<template>
  <b-form v-if="ready" class="public-invoice" @keydown="form.errors.clear($event.target.id)">
    <link
      href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap"
      rel="stylesheet"
    />

    <template v-if="!invalid && !completed && invoice">
      <header class="bg-white mb-3 shadow-sm fixed-top">
        <div class="container">
          <div class="row">
            <div class="col-9 col-sm-6 d-flex flex-wrap align-items-center">
              <div class="d-none d-lg-inline-block">Quote Total</div>
              <h2 class="pr-3 px-lg-3 header-total">${{ invoice.total }}</h2>
              <div class="text-muted header-date">
                Created {{ createdDate.format("Do MMM YYYY") }}
              </div>
            </div>
            <div class="col-3 col-sm-6 text-right py-4">
              <a
                v-if="!isAndroid"
                class="download-pdf cursor"
                target="_blank"
                @click.prevent="openPdf"
              >
                <span v-if="generatingPdf">
                  <span class="d-none d-lg-inline-block pr-2">Downloading</span>
                  <font-awesome-icon spin :icon="['fal', 'spinner']" />
                </span>
                <span v-else>
                  <span class="d-none d-lg-inline-block pr-2">Download PDF</span>
                  <font-awesome-icon :icon="['fal', 'cloud-download']" />
                </span>
              </a>

              <b-button
                v-if="invoice.accepted"
                variant="secondary"
                size="lg"
                class="d-none d-lg-inline-block cursor-not-allowed"
              >
                Accepted
              </b-button>
              <b-button
                v-else
                v-b-modal.acceptModal
                variant="danger"
                size="lg"
                class="d-none d-lg-inline-block"
              >
                Accept Quote
              </b-button>
            </div>
          </div>
        </div>
      </header>

      <footer class="fixed-bottom bg-white mt-3 px-3 py-4 shadow-lg d-lg-none">
        <div class="container">
          <b-button
            v-if="invoice.accepted"
            variant="secondary"
            size="lg"
            class="d-block w-100 cursor-not-allowed"
          >
            Accepted
          </b-button>
          <b-button v-else v-b-modal.acceptModal variant="danger" size="lg" class="d-block w-100">
            Accept Quote
          </b-button>
        </div>
      </footer>
    </template>

    <template v-if="completed">
      <div class="d-flex align-items-center justify-content-center position-absolute w-100 h-100">
        <div class="w-75 text-center shadow-sm bg-white rounded">
          <div class="flex w-100">
            <div class="flex text-center brand-header-content">
              <img
                :src="invoice.job.brand.logo"
                :alt="invoice.job.brand.name"
                class="brand-header-logo rounded-circle bg-white"
              />
              <h4 class="brand-header-name mb-1">{{ invoice.job.brand.name }}</h4>
              <div class="text-muted">
                Invoice: #{{ invoice.invoice_number | trimLeadingZeros }}
              </div>
              <div class="my-3">
                Thank you! The quote has been accepted.<br />
                <br />
                A member of our team will contact you to make an appointment.<br />
                Or You can call us on 13 11 49 to organise a technician visit.
              </div>
              <b-button
                variant="primary"
                class="d-none d-lg-inline-block mb-4"
                @click="$root.reload()"
              >
                View Quote Again
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-if="invalid && !completed">
      <div class="d-flex align-items-center justify-content-center position-absolute w-100 h-100">
        <div class="w-75 text-center shadow-sm bg-white rounded">
          <div class="flex w-100">
            <div class="flex text-center">
              <div class="mt-4 mb-4">The quote is invalid.</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal id="acceptModal" hide-footer centered>
      <template #modal-header="{ close }">
        <div class="flex w-100">
          <div class="flex text-center brand-header-content">
            <img
              :src="invoice.job.brand.logo"
              :alt="invoice.job.brand.name"
              class="brand-header-logo rounded-circle bg-white"
            />
            <h4 class="brand-header-name mb-1">{{ invoice.job.brand.name }}</h4>
            <div>Invoice: #{{ invoice.invoice_number | trimLeadingZeros }}</div>
          </div>
        </div>
        <button class="modal-close" @click="close()">
          <font-awesome-icon :icon="['fal', 'times']" />
        </button>
      </template>

      <div v-if="invoice && invoice.total">
        <div class="text-center">
          <div class="my-3">
            <div class="pt-3 mb-2">Please confirm to accept the quote for amount</div>
            <h1>
              <b>{{ formatCurrency(invoice.total) }}</b>
            </h1>
          </div>

          <div class="border border-light rounded">
            <VueSignaturePad
              id="signature"
              ref="signaturePad"
              width="100%"
              height="160px"
              :options="{
                onBegin: () => {
                  $refs.signaturePad.resizeCanvas();
                },
                onEnd,
              }"
            />
          </div>

          <button
            class="btn btn-lg mt-3 mb-4 w-100"
            :class="signed ? 'btn-danger' : 'btn-primary cursor-not-allowed'"
            :disabled="form.notReady() || !signed"
            @click.prevent="onSubmit"
          >
            <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
            <span v-else-if="signed">Confirm and Accept Quote</span>
            <span v-else>Please Sign Above</span>
          </button>
        </div>
      </div>
    </b-modal>

    <template v-if="!invalid && !completed && invoice">
      <div class="invoice-container container overflow-hidden py-3">
        <iframe :src="embedUrl"></iframe>
      </div>
    </template>
  </b-form>
</template>

<script type="text/babel">
import Vue from "vue";
import moment from "moment";
import VueSignaturePad from "vue-signature-pad";
import pageview from "../../imports/google";
import format from "../../mixins/format";
import iframe from "../../mixins/iframe";

Vue.use(VueSignaturePad);

export default {
  mixins: [format, iframe],

  data() {
    return {
      embedUrl: null,
      message: null,
      completed: false,
      invoice: false,
      invalid: false,

      signed: false,

      crud: null,
      form: new window.AppForm({
        accept: "",
        acceptedBy: this.$auth.user() && this.$auth.user().id ? this.$auth.user().id : 0,
        signature: null,
      }),
    };
  },

  computed: {
    createdDate() {
      return moment(this.invoice.created_at);
    },

    isAndroid() {
      return /(android)/i.test(navigator.userAgent);
    },
  },

  created() {
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      if (modalId === "acceptModal") {
        this.signed = false;
      }
    });

    const { uuid } = this.$router.currentRoute.params;

    this.crud = new window.AppCrud("invoice");

    // noinspection JSUnresolvedFunction
    this.$http
      .get(`/invoices/${uuid}/by-uuid`)
      .then((response) => {
        if (response) {
          this.invoice = response.body;
          this.embedUrl = `${process.env.VUE_APP_API}/invoices/${this.invoice.uuid}/preview-by-uuid`;

          pageview("/view-quote");

          // Browser Title
          if (this.$lodash.get(this, "invoice.invoice_number")) {
            // noinspection JSUnresolvedFunction
            this.$root.setTitle(
              `Accept Quote - ${this.$lodash.get(this, "invoice.invoice_number")}`
            );
          }

          // Maybe trigger modal display
          if (this.$router.currentRoute?.hash === "#show-accept") {
            this.$bvModal.show("acceptModal");
          }
        }
      })
      .catch(() => {
        this.invalid = true;
      });
  },

  methods: {
    onEnd() {
      this.signed = true;
    },

    onSubmit() {
      // Get signature
      const { data } = this.$refs.signaturePad.saveSignature();
      const { uuid } = this.$router.currentRoute.params;

      this.form.startProcessing();
      this.form.signature = data;
      this.form
        .post(`/invoices/${uuid}/accept-quote`)
        .then((response) => {
          if (response.accepted === true) {
            pageview("/quote-accepted");

            window.toastr.success("Successfully accepted the quote", "Success");
            this.$bvModal.hide("acceptModal");
            this.completed = true;
          }
        })
        .catch((response) => {
          this.message = response.message;

          this.form.reset();
          this.form.finishProcessing();
        });
    },
  },
};
</script>
